import React from 'react';

/* ------------------------------------------------------------------ */
/* Types des réponses de /v1/admin/discovered-avis                      */
/* ------------------------------------------------------------------ */

export interface LigneEnBase {
  id: number;
  exercice?: number | null;
  statut: string;
  montant_brut?: number | null;
  date_paiement?: string | null;
  credite: boolean;
}

export interface LigneBilan {
  num_avis: string;
  titre: string;
  symbole?: string | null;
  exercice?: number | null;
  convention: string;
  montant_affiche: number;
  montant_brut?: number | null;
  montant_net?: number | null;
  date_paiement?: string | null;
  issue: string;
  motif?: string | null;
  statut_cible?: string | null;
  dividende_id?: number | null;
  en_base: LigneEnBase[];
  alertes: string[];
  notes: string[];
}

export interface BilanCalendrier {
  simulation: boolean;
  lignes_attendues?: number | null;
  lignes_lues: number;
  lignes_perdues: string[];
  compteurs: Record<string, number>;
  a_revoir: boolean;
  lignes: LigneBilan[];
}

export interface DividendeExistant {
  id: number;
  exercice?: number | null;
  date_paiement?: string | null;
  montant_brut?: number | null;
  montant_net?: number | null;
  statut: string;
  credited_at?: string | null;
}

export interface Temoin {
  source: string;
  modele?: string | null;
  montant_publie?: number | null;
  nature_montant?: string | null;
  exercice_avis?: number | null;
  date_paiement?: string | null;
  citation?: string | null;
  citation_verifiee?: boolean | null;
}

export interface ExtractionAvis {
  avis_id: number;
  concordance: string;
  intention: string;
  societe_symbole?: string | null;
  societe_pays?: string | null;
  exercice?: number | null;
  montant_brut_propose?: number | null;
  montant_net_propose?: number | null;
  taux_irvm_applique?: number | null;
  date_paiement?: string | null;
  date_detachement?: string | null;
  date_annonce?: string | null;
  blocages: string[];
  temoins: Temoin[];
  dividendes_existants: DividendeExistant[];
  cumul_exercice_avant?: number | null;
  cumul_exercice_apres?: number | null;
}

export interface ExtractionMeta {
  deja_en_base?: boolean;
  avis_statut?: string;
  rapport_annuel_joint?: string | null;
}

export interface CompletudeLigne {
  symbole: string;
  nom_societe?: string | null;
  etat: string;
  statut?: string | null;
  montant_brut?: number | null;
  date_paiement?: string | null;
  nb_lignes: number;
  avis_ids: number[];
  avis_esv?: string | null;
  rapport_annuel?: string | null;
  rapport_annuel_url?: string | null;
}

export interface Completude {
  data: CompletudeLigne[];
  meta: {
    exercice: number;
    societes_cotees: number;
    compteurs: Record<string, number>;
    doublons: string[];
  };
}

/* ------------------------------------------------------------------ */
/* Libellés                                                             */
/* ------------------------------------------------------------------ */

const fmt = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 4 });
export const fcfa = (v?: number | null): string =>
  v === null || v === undefined ? '—' : `${fmt.format(v)} F`;
export const dateFr = (d?: string | null): string =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString('fr-FR') : '—';

const ISSUES: Record<string, { label: string; className: string }> = {
  DEJA_EN_BASE: { label: 'Déjà en base', className: 'issue-ok' },
  COMPLEMENT: { label: 'Déjà en base, complété', className: 'issue-ok' },
  INSERTION: { label: 'À insérer', className: 'issue-new' },
  DIVERGENCE: { label: 'Divergence', className: 'issue-ko' },
  NON_RESOLU: { label: 'Société non reconnue', className: 'issue-ko' },
  PAYS_INCONNU: { label: 'Pays inconnu', className: 'issue-ko' },
};

const BLOCAGES: Record<string, string> = {
  SYMBOLE_NON_RESOLU:
    "La société de l'avis n'est pas identifiée : corriger la société puis relancer.",
  MONTANT_ABSENT: "Aucun montant lu dans l'avis.",
  EXERCICE_ABSENT: "L'exercice n'est pas lu dans l'avis.",
  NATURE_MONTANT_INCONNUE: "L'avis ne dit pas si le montant est brut ou net.",
  DIVERGENCE_TEMOINS: 'Les lectures du PDF ne donnent pas le même montant.',
  CITATION_NON_VERIFIEE: 'La lecture automatique cite un passage absent du PDF.',
  EXCEPTIONNEL: 'Dividende exceptionnel : à saisir à part (taux d’IRVM majoré).',
  IRVM_DIVERGENT: "Le taux d'IRVM publié par l'avis diffère de la table de la plateforme.",
  PAYS_INCONNU: "Le pays de la société n'est pas renseigné.",
  DEJA_CREDITE: 'Un dividende de cet exercice est déjà crédité aux porteurs.',
};

const ETATS_COMPLETUDE: Record<string, { label: string; className: string }> = {
  DIVIDENDE: { label: 'En base', className: 'issue-ok' },
  AUCUN: { label: 'Aucun dividende', className: 'issue-neutral' },
  AVIS_NON_IMPORTE: { label: 'Avis non importé', className: 'issue-new' },
  RIEN: { label: 'Rien', className: 'issue-ko' },
};

const IssueBadge: React.FC<{
  issue: string;
  table?: Record<string, { label: string; className: string }>;
}> = ({ issue, table = ISSUES }) => {
  const c = table[issue] || { label: issue, className: 'issue-neutral' };
  return <span className={`issue-badge ${c.className}`}>{c.label}</span>;
};

/* ------------------------------------------------------------------ */
/* Bilan d'un calendrier : simulation puis application                  */
/* ------------------------------------------------------------------ */

interface BilanModalProps {
  titre: string;
  bilan: BilanCalendrier;
  isLoading: boolean;
  onAppliquer: () => void;
  onFermer: () => void;
}

export const BilanCalendrierModal: React.FC<BilanModalProps> = ({
  titre,
  bilan,
  isLoading,
  onAppliquer,
  onFermer,
}) => {
  const n = (k: string) => bilan.compteurs[k] || 0;
  const aEcrire = n('INSERTION') + n('COMPLEMENT');
  return (
    <div className='modal-backdrop-blur' onClick={isLoading ? undefined : onFermer}>
      <div
        className='caution-modal-box avis-wide-modal'
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
        aria-label={titre}
      >
        <div className='caution-modal-header neutral'>
          <i className='material-icons'>event_note</i>
          <h3>
            {bilan.simulation ? 'Simulation' : 'Résultat'} : {titre}
          </h3>
        </div>

        <div className='bilan-resume'>
          <span>
            <strong>{bilan.lignes_lues}</strong> lignes lues
            {bilan.lignes_attendues != null && (
              <>
                {' '}
                sur <strong>{bilan.lignes_attendues}</strong> citées par le PDF
              </>
            )}
          </span>
          <span className='issue-badge issue-ok'>
            {n('DEJA_EN_BASE') + n('COMPLEMENT')} déjà en base
          </span>
          <span className='issue-badge issue-new'>
            {n('INSERTION')} {bilan.simulation ? 'à insérer' : 'insérés'}
          </span>
          {n('DIVERGENCE') + n('NON_RESOLU') + n('PAYS_INCONNU') > 0 && (
            <span className='issue-badge issue-ko'>
              {n('DIVERGENCE') + n('NON_RESOLU') + n('PAYS_INCONNU')} à revoir
            </span>
          )}
        </div>

        {bilan.lignes_perdues.length > 0 && (
          <div className='caution-callout-box'>
            <strong>Lignes du PDF non lues :</strong> avis {bilan.lignes_perdues.join(', ')}. Le
            calendrier restera « En attente revue ».
          </div>
        )}

        <div className='bilan-table-wrap'>
          <table className='bilan-table'>
            <thead>
              <tr>
                <th>Avis</th>
                <th>Société</th>
                <th>Publié</th>
                <th>Brut</th>
                <th>Paiement</th>
                <th>Issue</th>
                <th>Détail</th>
              </tr>
            </thead>
            <tbody>
              {bilan.lignes.map((l) => (
                <tr key={l.num_avis} className={l.alertes.length ? 'row-alerte' : ''}>
                  <td>{l.num_avis.replace(' BRVMDG', '')}</td>
                  <td title={l.titre}>
                    <strong>{l.symbole || '??'}</strong>
                  </td>
                  <td>
                    {fcfa(l.montant_affiche)}{' '}
                    <small>{l.convention === 'BRUT' ? 'brut' : 'net'}</small>
                  </td>
                  <td>{fcfa(l.montant_brut)}</td>
                  <td>{dateFr(l.date_paiement)}</td>
                  <td>
                    <IssueBadge issue={l.issue} />
                    {l.issue === 'INSERTION' && l.statut_cible && (
                      <small> en {l.statut_cible === 'PAYE' ? 'Payé' : 'Annoncé'}</small>
                    )}
                  </td>
                  <td className='bilan-detail'>
                    {l.motif && <div>{l.motif}</div>}
                    {l.alertes.map((a, i) => (
                      <div key={`a${i}`} className='txt-alerte'>
                        {a}
                      </div>
                    ))}
                    {l.notes.map((a, i) => (
                      <div key={`n${i}`} className='txt-note'>
                        {a}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className='bilan-legende'>
          Un dividende déjà en base n'est jamais réécrit ; un montant différent n'est jamais écrasé
          (divergence à trancher sur l'avis de paiement). Aucun porteur n'est crédité par cette
          opération.
        </p>

        <div className='modal-actions-bar'>
          <button
            onClick={onFermer}
            disabled={isLoading}
            className='btn-table-action btn-action-secondary'
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            Fermer
          </button>
          {bilan.simulation && (
            <button
              onClick={onAppliquer}
              disabled={isLoading || aEcrire === 0}
              className='btn-table-action btn-action-process'
              style={{ padding: '8px 18px', fontSize: '13px' }}
              title={aEcrire === 0 ? 'Rien à écrire : tout est déjà en base' : undefined}
            >
              <i className={`material-icons ${isLoading ? 'spin-anim' : ''}`}>
                {isLoading ? 'sync' : 'save'}
              </i>
              {aEcrire === 0
                ? 'Rien à écrire'
                : `Écrire ${n('INSERTION')} dividende(s) et compléter ${n('COMPLEMENT')} ligne(s)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Avis de paiement : proposition d'import                              */
/* ------------------------------------------------------------------ */

interface ExtractionModalProps {
  titre: string;
  extraction: ExtractionAvis;
  meta: ExtractionMeta;
  isLoading: boolean;
  onImporter: () => void;
  onFermer: () => void;
}

export const ExtractionAvisModal: React.FC<ExtractionModalProps> = ({
  titre,
  extraction: x,
  meta,
  isLoading,
  onImporter,
  onFermer,
}) => {
  const dejaEnBase = Boolean(meta.deja_en_base);
  const importable =
    !dejaEnBase &&
    x.blocages.length === 0 &&
    (x.intention === 'CREATION' || x.intention === 'REVISION');
  const revision = x.intention === 'REVISION';
  const avant = x.dividendes_existants.find((d) => d.statut === 'ANNONCE');

  return (
    <div className='modal-backdrop-blur' onClick={isLoading ? undefined : onFermer}>
      <div
        className='caution-modal-box avis-wide-modal'
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
        aria-label={titre}
      >
        <div className='caution-modal-header neutral'>
          <i className='material-icons'>payments</i>
          <h3>{titre}</h3>
        </div>

        {dejaEnBase ? (
          <div className='caution-callout-box ok'>
            <strong>Dividende déjà en base.</strong> {x.societe_symbole} exercice {x.exercice} :{' '}
            {fcfa(x.montant_brut_propose)} brut, identique à la base. Rien n'est écrit ; l'avis est
            marqué « Traité ».
          </div>
        ) : x.blocages.length > 0 ? (
          <div className='caution-callout-box'>
            <strong>Import impossible en l'état :</strong>
            <ul className='liste-blocages'>
              {x.blocages.map((b) => (
                <li key={b}>{BLOCAGES[b] || b}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className='extraction-grid'>
          <div>
            <span>Société</span>
            <strong>{x.societe_symbole || '—'}</strong> <small>{x.societe_pays || ''}</small>
          </div>
          <div>
            <span>Exercice</span>
            <strong>{x.exercice ?? '—'}</strong>
          </div>
          <div>
            <span>Brut par action</span>
            <strong>{fcfa(x.montant_brut_propose)}</strong>
          </div>
          <div>
            <span>Net personnes physiques</span>
            <strong>{fcfa(x.montant_net_propose)}</strong>
            {x.taux_irvm_applique != null && (
              <small> IRVM {fmt.format(x.taux_irvm_applique)} %</small>
            )}
          </div>
          <div>
            <span>Détachement</span>
            <strong>{dateFr(x.date_detachement)}</strong>
          </div>
          <div>
            <span>Paiement</span>
            <strong>{dateFr(x.date_paiement)}</strong>
          </div>
        </div>

        <div className='bilan-table-wrap'>
          <table className='bilan-table'>
            <thead>
              <tr>
                <th>Lecture</th>
                <th>Montant</th>
                <th>Nature</th>
                <th>Exercice</th>
                <th>Citation</th>
              </tr>
            </thead>
            <tbody>
              {x.temoins.map((t, i) => (
                <tr key={i}>
                  <td>{t.source === 'LLM' ? `IA (${t.modele || 'modèle'})` : 'Texte du PDF'}</td>
                  <td>{fcfa(t.montant_publie)}</td>
                  <td>{t.nature_montant || '—'}</td>
                  <td>{t.exercice_avis ?? '—'}</td>
                  <td className='bilan-detail'>
                    {t.citation_verifiee === false ? (
                      <span className='txt-alerte'>
                        Citation introuvable dans le PDF : lecture écartée
                      </span>
                    ) : t.citation ? (
                      `« ${t.citation} »`
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {x.dividendes_existants.length > 0 && (
          <p className='bilan-legende'>
            En base pour {x.societe_symbole}/{x.exercice} :{' '}
            {x.dividendes_existants
              .map((d) => `${d.statut} ${fcfa(d.montant_brut)}${d.credited_at ? ' (crédité)' : ''}`)
              .join(' ; ')}
            .
          </p>
        )}
        {meta.rapport_annuel_joint && (
          <p className='bilan-legende'>
            Rapport annuel joint en corroboration : {meta.rapport_annuel_joint}.
          </p>
        )}

        <div className='modal-actions-bar'>
          <button
            onClick={onFermer}
            disabled={isLoading}
            className='btn-table-action btn-action-secondary'
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            Fermer
          </button>
          {importable && (
            <button
              onClick={onImporter}
              disabled={isLoading}
              className='btn-table-action btn-action-process'
              style={{ padding: '8px 18px', fontSize: '13px' }}
            >
              <i className={`material-icons ${isLoading ? 'spin-anim' : ''}`}>
                {isLoading ? 'sync' : 'save'}
              </i>
              {revision
                ? `Réviser le dividende annoncé : ${fcfa(avant?.montant_brut)} → ${fcfa(x.montant_brut_propose)} brut`
                : `Enregistrer ${fcfa(x.montant_brut_propose)} brut en « Annoncé »`}
            </button>
          )}
        </div>
        {importable && (
          <p className='bilan-legende'>
            Le dividende est enregistré en statut « Annoncé » : aucun porteur n'est crédité. Il
            change le rendement affiché dès l'écriture.
          </p>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Complétude d'un exercice                                             */
/* ------------------------------------------------------------------ */

interface CompletudeModalProps {
  completude: Completude | null;
  exercice: number;
  isLoading: boolean;
  onExercice: (ex: number) => void;
  onFermer: () => void;
}

export const CompletudeModal: React.FC<CompletudeModalProps> = ({
  completude,
  exercice,
  isLoading,
  onExercice,
  onFermer,
}) => {
  const annee = new Date().getFullYear();
  const choix = [annee - 1, annee - 2, annee - 3];
  return (
    <div className='modal-backdrop-blur' onClick={onFermer}>
      <div
        className='caution-modal-box avis-wide-modal'
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
        aria-label='Complétude des dividendes'
      >
        <div className='caution-modal-header neutral'>
          <i className='material-icons'>fact_check</i>
          <h3>Complétude des dividendes — exercice</h3>
          <select
            value={exercice}
            onChange={(e) => onExercice(Number(e.target.value))}
            className='filter-control'
            style={{ width: 'auto', marginLeft: 8 }}
          >
            {choix.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>

        {isLoading || !completude ? (
          <div className='table-empty-state'>
            <i className='material-icons spin-anim'>sync</i>
            <h3>Chargement…</h3>
          </div>
        ) : (
          <>
            <div className='bilan-resume'>
              <span>
                <strong>{completude.meta.societes_cotees}</strong> sociétés cotées
              </span>
              {Object.entries(completude.meta.compteurs).map(([k, v]) => (
                <span
                  key={k}
                  className={`issue-badge ${(ETATS_COMPLETUDE[k] || { className: 'issue-neutral' }).className}`}
                >
                  {v} {(ETATS_COMPLETUDE[k] || { label: k }).label.toLowerCase()}
                </span>
              ))}
            </div>
            {completude.meta.doublons.length > 0 && (
              <div className='caution-callout-box'>
                <strong>Plusieurs lignes pour un même exercice :</strong>{' '}
                {completude.meta.doublons.join(', ')}. Un seul dividende annuel par exercice :
                doublon probable.
              </div>
            )}
            <div className='bilan-table-wrap'>
              <table className='bilan-table'>
                <thead>
                  <tr>
                    <th>Société</th>
                    <th>État</th>
                    <th>Brut</th>
                    <th>Paiement</th>
                    <th>Piste</th>
                  </tr>
                </thead>
                <tbody>
                  {completude.data.map((l) => (
                    <tr key={l.symbole} className={l.nb_lignes > 1 ? 'row-alerte' : ''}>
                      <td title={l.nom_societe || ''}>
                        <strong>{l.symbole}</strong>
                      </td>
                      <td>
                        <IssueBadge issue={l.etat} table={ETATS_COMPLETUDE} />
                        {l.statut && l.etat === 'DIVIDENDE' && (
                          <small> {l.statut === 'PAYE' ? 'Payé' : 'Annoncé'}</small>
                        )}
                      </td>
                      <td>{l.etat === 'DIVIDENDE' ? fcfa(l.montant_brut) : '—'}</td>
                      <td>{dateFr(l.date_paiement)}</td>
                      <td className='bilan-detail'>
                        {l.avis_ids.length > 0 && (
                          <div>Avis détecté n° {l.avis_ids.join(', ')}</div>
                        )}
                        {l.avis_esv && (
                          <div>
                            <a href={l.avis_esv} target='_blank' rel='noreferrer'>
                              Avis de paiement (ESV)
                            </a>
                          </div>
                        )}
                        {l.rapport_annuel && (
                          <div>
                            Rapport annuel :{' '}
                            {l.rapport_annuel_url ? (
                              <a href={l.rapport_annuel_url} target='_blank' rel='noreferrer'>
                                {l.rapport_annuel}
                              </a>
                            ) : (
                              l.rapport_annuel
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className='bilan-legende'>
              « Rien » ne veut pas dire « pas de dividende » : le dividende peut ne pas être encore
              annoncé. Le rapport annuel est une piste, jamais une source d'écriture.
            </p>
          </>
        )}

        <div className='modal-actions-bar'>
          <button
            onClick={onFermer}
            className='btn-table-action btn-action-secondary'
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};
